import type { BallState } from '../sim/state';
import type { SimConfig } from '../config/simConfig';
import type { PitchDims } from '../config/pitchConfig';
import type { Vec3 } from '../types';
import { POST_R } from '../config/pitchConfig';

// Ball vs world geometry (§5.5/§6.2): goalposts (vertical capsules) and the goal predicate. Pitch
// plane bounce lives in physics/ball.ts; nets/crossbar detail arrives with the rules slice (M3).

/**
 * Bounce the ball off the four goalposts (circles of radius POST_R at the goal-mouth corners) with
 * REST_POST restitution. Only while the ball is below the crossbar. Mutates `ball`.
 */
export function resolveBallPosts(ball: BallState, pitch: PitchDims, cfg: SimConfig): void {
  if (ball.pos.z > pitch.barH) return; // above the goal frame
  const gx = pitch.length / 2;
  const gy = pitch.goalWidth / 2;
  const reach = cfg.ball.RADIUS + POST_R;
  for (const px of [-gx, gx]) {
    for (const py of [-gy, gy]) {
      const dx = ball.pos.x - px;
      const dy = ball.pos.y - py;
      const dist = Math.hypot(dx, dy);
      if (dist >= reach) continue;
      const nx = dist > 0 ? dx / dist : 1;
      const ny = dist > 0 ? dy / dist : 0;
      ball.pos.x = px + nx * reach;
      ball.pos.y = py + ny * reach;
      const vn = ball.vel.x * nx + ball.vel.y * ny;
      if (vn < 0) {
        const k = (1 + cfg.ball.REST_POST) * vn;
        ball.vel.x -= k * nx;
        ball.vel.y -= k * ny;
      }
    }
  }
}

/**
 * Goal predicate (§6.2): the WHOLE ball over a goal line (center past the line by the ball radius),
 * between the posts (|y| < GOAL_HALF_W − POST_R), below the crossbar. Returns 0 = no goal, +1 = over
 * the +x goal line, −1 = over the −x line.
 */
export function goalOver(ballPos: Vec3, pitch: PitchDims, ballRadius: number): 0 | 1 | -1 {
  if (ballPos.z >= pitch.barH) return 0;
  if (Math.abs(ballPos.y) >= pitch.goalWidth / 2 - POST_R) return 0;
  const gx = pitch.length / 2;
  if (ballPos.x - ballRadius > gx) return 1; // whole ball fully over the +x line
  if (ballPos.x + ballRadius < -gx) return -1;
  return 0;
}

export function isBallInGoal(ballPos: Vec3, pitch: PitchDims, ballRadius: number): boolean {
  return goalOver(ballPos, pitch, ballRadius) !== 0;
}
